import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Grid,
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
  useTheme
} from '@mui/material';
import { loadWallpapers } from '../constants/wallpaperConstants';

const WallpaperGallery = ({ open, onClose, onSelect, selectedWallpaper }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [wallpapers, setWallpapers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load wallpapers when the dialog is opened
  useEffect(() => {
    if (open) {
      setLoading(true);
      loadWallpapers()
        .then(loadedWallpapers => {
          setWallpapers(loadedWallpapers);
          setLoading(false);
        })
        .catch(error => {
          console.error('Error loading wallpapers:', error);
          setLoading(false);
          setWallpapers([{
            id: 'default',
            name: 'No Wallpaper',
            path: null,
            thumbnail: null,
            description: 'Default chat background'
          }]);
        });
    }
  }, [open]);

  const handleSelect = (wallpaperId) => {
    onSelect(wallpaperId);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        {t('settings.design.selectWallpaper', { defaultValue: 'Select Wallpaper' })}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {/* No Wallpaper option */}
            <Grid item xs={6} sm={4} md={3} lg={2}>
              <Paper
                elevation={3}
                sx={{
                  position: 'relative',
                  height: 140,
                  cursor: 'pointer',
                  border: selectedWallpaper === 'default' ? 
                    `2px solid ${theme.palette.primary.main}` : 
                    '2px solid transparent',
                  backgroundColor: theme.palette.background.paper,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: theme.shadows[5]
                  }
                }}
                onClick={() => handleSelect('default')}
              >
                <Typography variant="caption" align="center">
                  {t('settings.design.noWallpaper')}
                </Typography>
              </Paper>
            </Grid>

            {/* Organize wallpapers by file type */}
            {wallpapers.filter(wp => wp.id !== 'default').map((wallpaper) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={wallpaper.id}>
                <Paper
                  elevation={3}
                  sx={{
                    position: 'relative',
                    height: 140,
                    cursor: 'pointer',
                    border: selectedWallpaper === wallpaper.id ? 
                      `2px solid ${theme.palette.primary.main}` : 
                      '2px solid transparent',
                    backgroundColor: 'transparent',
                    backgroundImage: wallpaper.thumbnail ? 
                      `url(${wallpaper.thumbnail})` : 
                      'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: theme.shadows[5]
                    }
                  }}
                  onClick={() => handleSelect(wallpaper.id)}
                >
                  {/* Format badge */}
                  <Box 
                    sx={{
                      position: 'absolute',
                      top: 5,
                      right: 5,
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      borderRadius: '4px',
                      padding: '2px 4px',
                      fontSize: '0.6rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {wallpaper.extension ? wallpaper.extension.toUpperCase().replace('.', '') : 
                      wallpaper.id.split('.').pop().toUpperCase()}
                  </Box>
                  
                  <Box sx={{
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    width: '100%',
                    p: 0.5,
                  }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: '#fff',
                        textAlign: 'center',
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {wallpaper.name}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t('cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WallpaperGallery;